// OWNER: Agent C. The screen that matters most: holding picker → type → date
// → quantity + rate (or a plain amount for cash flows) → a live balance
// preview, then submit. Enter submits from anywhere in the form. Withdrawals
// are first-class, not an afterthought.
import { useMemo, useState, type FormEvent } from "react";
import { useLocation, useNavigate } from "react-router";
import { Text } from "@cloudflare/kumo/components/text";
import { Button } from "@cloudflare/kumo/components/button";
import { Select } from "@cloudflare/kumo/components/select";
import { Input } from "@cloudflare/kumo/components/input";
import { useKumoToastManager } from "@cloudflare/kumo/components/toast";
import type { Currency, TransactionType } from "@shared/types";
import { formatMoney, formatUnits } from "@shared/format";
import { useAllHoldingsByAccount, createTransaction } from "../../lib/hooks";
import { parseAmountInput } from "../../lib/numberInput";
import { HoldingPicker, buildHoldingGroups, type HoldingOption } from "../../components/HoldingPicker";
import { PageHeader } from "../../components/PageHeader";
import { CardSkeleton } from "../../components/Skeletons";

const TXN_TYPES: Record<string, string> = {
  BUY: "Buy",
  SELL: "Sell",
  CONTRIBUTION: "Contribute",
  WITHDRAWAL: "Withdraw",
  DIVIDEND: "Dividend",
  INTEREST: "Interest",
  FEE: "Fee",
  VEST: "Vest",
};

const WITHDRAWAL_TYPES = new Set(["SELL", "WITHDRAWAL"]);
// Types that move `units` (or, for VEST, `vestedUnits`) on a UNITS_X_PRICE
// holding — the form asks for quantity + rate instead of a flat amount.
const UNIT_TYPES = new Set(["BUY", "SELL", "CONTRIBUTION", "WITHDRAWAL", "VEST"]);

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

const CURRENCY_ITEMS: Record<Currency, string> = { INR: "₹ INR", GBP: "£ GBP", USD: "$ USD" };

/**
 * Always locked to the picked holding's own currency — a holding's units and
 * price only mean something in the currency it actually trades in (a UK
 * ETF's price is quoted in GBP on the LSE; there's no "buy it at a USD
 * rate"). This is a visible indicator, not a free choice: letting it diverge
 * from the holding's currency would save a rate with no FX conversion
 * applied anywhere, silently corrupting that holding's cost tracking.
 */
function CurrencyField({ currency }: { currency: Currency | undefined }) {
  return (
    <Select
      label="Currency"
      aria-label="Currency (fixed to the holding's own currency)"
      disabled
      value={currency ?? null}
      items={CURRENCY_ITEMS}
      placeholder="—"
    />
  );
}

interface PreviewResult {
  label: string;
  before: string;
  after: string;
  insufficientBalance: boolean;
}

export function computePreview(option: HoldingOption, type: string, amount: number, units: number): PreviewResult | null {
  const h = option.holding;
  // Vesting doesn't touch `units` (that only happens on EXERCISE) — it
  // credits `vestedUnits`, a separate running total.
  if (type === "VEST") {
    const before = h.vestedUnits ?? 0;
    return {
      label: "Vested units",
      before: formatUnits(before),
      after: formatUnits(before + units),
      insufficientBalance: false,
    };
  }
  if (h.valuationMode === "CASH_BALANCE") {
    const before = h.cashBalance;
    let after = before;
    if (type === "CONTRIBUTION" || type === "DIVIDEND" || type === "INTEREST") after = before + amount;
    if (type === "WITHDRAWAL") after = before - amount;
    return {
      label: "Cash balance",
      before: formatMoney(before, h.currency),
      after: formatMoney(after, h.currency),
      insufficientBalance: type === "WITHDRAWAL" && amount > before,
    };
  }
  if (h.valuationMode === "UNITS_X_PRICE" && UNIT_TYPES.has(type)) {
    const before = h.units;
    let after = before;
    if (type === "BUY" || type === "CONTRIBUTION") after = before + units;
    if (type === "SELL" || type === "WITHDRAWAL") after = before - units;
    return {
      label: "Units",
      before: formatUnits(before),
      after: formatUnits(after),
      insufficientBalance: WITHDRAWAL_TYPES.has(type) && units > before,
    };
  }
  return null;
}

export default function NewTransactionPage() {
  const holdingsQuery = useAllHoldingsByAccount();
  const navigate = useNavigate();
  const location = useLocation();
  const toasts = useKumoToastManager();
  const preselectHoldingId = (location.state as { holdingId?: number } | null)?.holdingId;

  const [option, setOption] = useState<HoldingOption | null>(null);
  const [type, setType] = useState<TransactionType>("BUY");
  const [date, setDate] = useState(today());
  const [amount, setAmount] = useState(""); // cash-flow transactions only (no quantity/rate)
  const [quantity, setQuantity] = useState(""); // number of units/shares
  const [rate, setRate] = useState(""); // price of a single unit/share
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const groups = useMemo(
    () => (holdingsQuery.status === "success" ? buildHoldingGroups(holdingsQuery.data.accounts, holdingsQuery.data.holdingsByAccount) : []),
    [holdingsQuery],
  );

  // Preselect once holdings have loaded (came from "Add transaction" on a holding page).
  useMemo(() => {
    if (option || !preselectHoldingId || holdingsQuery.status !== "success") return;
    for (const group of groups) {
      const match = group.items.find((i) => i.holding.id === preselectHoldingId);
      if (match) {
        setOption(match);
        break;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groups, preselectHoldingId, holdingsQuery.status]);

  // Quantity × rate replaces a hand-typed amount for anything priced in
  // units — asking for a flat total invited a total that didn't actually
  // match units × price, with nothing to catch the mismatch.
  const showQuantity = option?.holding.valuationMode === "UNITS_X_PRICE" && UNIT_TYPES.has(type);
  const currency = option?.holding.currency;
  // Amount fields accept comma thousands-separators for readability
  // ("1,20,2893.323"); parseAmountInput strips them and returns null for
  // anything else invalid, so a garbled amount fails validation instead of
  // silently becoming NaN/0 downstream.
  const quantityNum = parseAmountInput(quantity) ?? 0;
  const rateNum = parseAmountInput(rate) ?? 0;
  const totalValue = quantityNum * rateNum;
  const parsedAmount = parseAmountInput(amount);
  const amountNum = showQuantity ? totalValue : (parsedAmount ?? 0);

  const previewReady = option && (showQuantity ? quantity && rate : amount);
  const preview = previewReady ? computePreview(option!, type, amountNum, quantityNum) : null;

  function validate(): boolean {
    const next: Record<string, string> = {};
    if (!option) next.holding = "Pick a holding";
    if (!date) next.date = "Pick a date";
    if (showQuantity) {
      if (!quantity || parseAmountInput(quantity) === null || quantityNum <= 0) next.quantity = "Enter the number of units";
      if (!rate || parseAmountInput(rate) === null || rateNum <= 0) next.rate = "Enter the rate per unit";
    } else if (!amount || parsedAmount === null || amountNum <= 0) {
      next.amount = "Enter an amount greater than zero";
    }
    if (preview?.insufficientBalance) {
      const key = showQuantity ? "quantity" : "amount";
      next[key] = `That exceeds the current ${preview.label.toLowerCase()}`;
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!validate() || !option) return;
    setSubmitting(true);
    try {
      await createTransaction({
        holdingId: option.holding.id,
        type,
        txnDate: date,
        amount: amountNum,
        units: showQuantity ? quantityNum : null,
        price: showQuantity ? rateNum : null,
        note: note || null,
      });
      toasts.add({ title: "Transaction recorded", variant: "success" });
      navigate(`/investments/${option.holding.id}`);
    } catch (e2) {
      const message = e2 instanceof Error ? e2.message : "Something went wrong";
      setErrors((prev) => ({ ...prev, submit: message }));
      toasts.add({ title: "Couldn't save transaction", content: message, variant: "error" });
    } finally {
      setSubmitting(false);
    }
  }

  if (holdingsQuery.status === "loading") return <CardSkeleton />;
  if (holdingsQuery.status === "error") return <Text variant="error">{holdingsQuery.error.message}</Text>;

  return (
    <div className="max-w-xl pb-8">
      <PageHeader title="Add transaction" description="Buy, sell, contribute, withdraw, vest, or record income — Enter submits." />

      <form className="space-y-5" onSubmit={handleSubmit} noValidate>
        <HoldingPicker groups={groups} value={option} onValueChange={setOption} error={errors.holding} />

        <Select
          label="Type"
          required
          value={type}
          onValueChange={(v) => setType((v as TransactionType) ?? "BUY")}
          items={TXN_TYPES}
        />

        <Input label="Date" type="date" required value={date} onChange={(e) => setDate(e.target.value)} error={errors.date} />

        {showQuantity ? (
          <>
            <div className="grid grid-cols-3 gap-4">
              <Input
                label="Number of units"
                type="text"
                inputMode="decimal"
                required
                placeholder="0.0000"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                error={errors.quantity}
              />
              <Input
                label="Rate per unit"
                type="text"
                inputMode="decimal"
                required
                placeholder="0.0000"
                value={rate}
                onChange={(e) => setRate(e.target.value)}
                error={errors.rate}
              />
              <CurrencyField currency={currency} />
            </div>
            {quantity && rate && currency && (
              <Text variant="secondary" size="sm">
                Total: {formatMoney(totalValue, currency)}
              </Text>
            )}
          </>
        ) : (
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Amount"
              type="text"
              inputMode="decimal"
              required
              placeholder="0.00"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              error={errors.amount}
            />
            <CurrencyField currency={currency} />
          </div>
        )}

        <Input label="Note" required={false} placeholder="Optional" value={note} onChange={(e) => setNote(e.target.value)} />

        {preview && (
          <div
            className={`rounded-lg border p-4 ${preview.insufficientBalance ? "border-kumo-danger bg-kumo-danger-tint" : "border-kumo-hairline"}`}
            aria-live="polite"
          >
            <Text variant="secondary" size="sm">
              {preview.label} after this transaction
            </Text>
            <div className="mt-1 flex items-center gap-2 text-base">
              <span className="text-kumo-subtle line-through">{preview.before}</span>
              <span>→</span>
              <span className={preview.insufficientBalance ? "text-kumo-danger" : "text-kumo-strong font-medium"}>{preview.after}</span>
            </div>
            {preview.insufficientBalance && (
              <div className="mt-1">
                <Text variant="error" size="xs">
                  This would go below zero.
                </Text>
              </div>
            )}
          </div>
        )}

        {errors.submit && <Text variant="error">{errors.submit}</Text>}

        <div className="flex gap-3">
          <Button type="submit" variant="primary" loading={submitting}>
            Save transaction
          </Button>
          <Button type="button" variant="secondary" onClick={() => navigate(-1)}>
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
}
